"use client";

import { ArrowDown2, User } from "iconsax-reactjs";
import { useTranslations } from "next-intl";
import type React from "react";
import { useEffect, useState } from "react";
import { useFetchMe } from "@/hooks";
import { Link, usePathname } from "@/i18n/navigation";
import { cn } from "@/lib/utils";
import { LocaleSwitcher } from "../LocaleSwitcher";
import { isNavDestinationActive, NAV_DESTINATIONS } from "../nav-destinations";
import { SideNavAccountLinks } from "./SideNavAccountLinks";

interface SideNavProps {
  className?: string;
  initialPath?: string;
}

/** The `/user/me` role widens to include "DEV"; ProfileLinks only accepts these. */
type ProfileRole = "USER" | "ADMIN" | "GUEST";

const toProfileRole = (
  role: "USER" | "ADMIN" | "GUEST" | "DEV" | undefined,
): ProfileRole | undefined => {
  if (role === "DEV") return "ADMIN";
  return role;
};

const isUnderProfile = (path: string): boolean =>
  path === "/profile" || path.startsWith("/profile/");

/**
 * Global desktop side-nav (lg+). Renders the 5 primary NAV_DESTINATIONS plus an
 * expandable "Account" section that reuses ProfileLinks. On mobile/tablet these
 * destinations live in the BottomNav, so this is hidden below `lg`.
 */
export const SideNav: React.FC<SideNavProps> = ({ className, initialPath }) => {
  const t = useTranslations("nav");
  const pathname = usePathname();
  const currentPath = pathname || initialPath || "";

  const { data } = useFetchMe();
  const role = toProfileRole(data?.role);

  const primaryDestinations = NAV_DESTINATIONS.filter(
    (d) => d.href !== "/profile",
  );

  const profileActive = isNavDestinationActive(currentPath, "/profile");
  const [accountOpen, setAccountOpen] = useState(isUnderProfile(currentPath));

  useEffect(() => {
    if (isUnderProfile(currentPath)) setAccountOpen(true);
  }, [currentPath]);

  return (
    <aside
      className={cn(
        "hidden lg:flex lg:flex-col",
        "w-60 shrink-0",
        // Float the menu away from the viewport edge (RTL inline-start = right)
        // and let it clear the non-sticky TopNav. It is NOT a full-height rail.
        "ps-5 pe-2 py-4",
        "sticky top-4 self-start",
        className,
      )}
    >
      {/* Borderless, airy column — no bordered "card" so the rail reads as
          storefront chrome, not a dashboard panel. Whitespace + the grid
          gutter do the separating. */}
      <div className="flex max-h-[calc(100dvh-3rem)] flex-col gap-1 overflow-y-auto pe-1">
        <div className="mb-3 px-1">
          <LocaleSwitcher />
        </div>

        <nav className="flex flex-col gap-1">
          {primaryDestinations.map(({ href, icon: Icon, labelKey }) => {
            const active = isNavDestinationActive(currentPath, href);
            return (
              <Link
                key={href}
                href={href}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "flex items-center gap-3 rounded-xl px-3 py-2.5",
                  "text-sm font-medium transition-colors",
                  active
                    ? "text-accent bg-bg-secondary"
                    : "text-text-secondary hover:text-text-primary hover:bg-bg-secondary/60",
                )}
              >
                <Icon
                  variant={active ? "Bold" : "Linear"}
                  size={20}
                  className="shrink-0"
                />
                <span>{t(labelKey)}</span>
              </Link>
            );
          })}
        </nav>

        {/* Expandable Account / پروفایل section (rail-native rows) */}
        <div>
          <button
            type="button"
            aria-expanded={accountOpen}
            onClick={() => setAccountOpen((open) => !open)}
            className={cn(
              "w-full flex items-center justify-between gap-3 rounded-xl px-3 py-2.5",
              "text-sm font-medium transition-colors",
              profileActive
                ? "text-accent bg-bg-secondary"
                : "text-text-secondary hover:text-text-primary hover:bg-bg-secondary/60",
            )}
          >
            <span className="flex items-center gap-3">
              <User
                variant={profileActive ? "Bold" : "Linear"}
                size={20}
                className="shrink-0"
              />
              <span>{t("account")}</span>
            </span>
            <ArrowDown2
              size={16}
              className={cn(
                "shrink-0 transition-transform",
                accountOpen && "rotate-180",
              )}
            />
          </button>

          {accountOpen && (
            <SideNavAccountLinks role={role} currentPath={currentPath} />
          )}
        </div>
      </div>
    </aside>
  );
};
